"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { OptimizedImage } from "@/components/ui/optimized-image"
import Image from "next/image"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import type { NFTCollection } from "@/lib/api"
import { generatePlaceholderImage } from "@/lib/image-utils"
import { getOpenSeaUrl } from "@/lib/opensea-utils"
import { debug } from "@/lib/debug"

interface CollectionInfoProps {
  collection: NFTCollection | { id: string; name: string; image?: string; chain?: "ethereum" | "base" | "ronin" }
  children: React.ReactNode
  isBaseCube?: boolean
}

export function CollectionInfo({ collection, children, isBaseCube = false }: CollectionInfoProps) {
  const [isMobile, setIsMobile] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || ""
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase()))
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)
    return () => window.removeEventListener("resize", checkMobile)
  }, [])

  // Don't show info for base cube
  if (isBaseCube) {
    return <>{children}</>
  }

  // Content to display in tooltip or dialog
  const CollectionContent = () => (
    <div className="flex items-center space-x-4">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-gray-100">
        <OptimizedImage
          src={collection.image || `/placeholder.svg?height=120&width=120&query=${encodeURIComponent(collection.name)}`}
          alt={collection.name}
          width={120}
          height={120}
          className="rounded-lg object-cover"
          fallbackFormats={["webp", "png", "jpg"]}
          placeholderText={collection.name}
        />
      </div>
      <div className="flex-1">
        <h3 className="text-gray-900 font-medium font-funnel text-lg">{collection.name}</h3>
        {"tokenCount" in collection && collection.tokenCount && (
          <p className="text-gray-600">Owned: {collection.tokenCount}</p>
        )}
        {/*{collection.chain && (
          <p className="text-gray-600 text-xs">Chain: {collection.chain === "base" ? "Base" : "Ethereum"}</p>
        )}*/}
      </div>
    </div>
  )

  // For mobile: use Dialog
  if (isMobile) {
    return (
      <>
        <div className="cursor-pointer" onClick={() => setDialogOpen(true)}>
          {children}
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md p-6 px-4 mx-4 bg-white rounded-xl">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-gray-100">
                <Image
                  src={collection.image || generatePlaceholderImage(collection.name, "avatar")}
                  alt={collection.name}
                  width={120}
                  height={120}
                  className="rounded-lg object-cover"
                  onError={() => {
                    // If image fails to load, use a placeholder
                    if (collection.image && !collection.image.includes("placeholder.svg")) {
                      debug(`Failed to load avatar image for collection: ${collection.id}`)
                      collection.image = generatePlaceholderImage(collection.name, "avatar")
                    }
                  }}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 font-medium font-funnel text-lg">{collection.name}</h3>
                {"tokenCount" in collection && collection.tokenCount && (
                  <p className="text-gray-600">Owned: {collection.tokenCount}</p>
                )}
                {/*{collection.chain && (
                    <p className="text-gray-600 text-xs">Chain: {collection.chain === "base" ? "Base" : "Ethereum"}</p>
                  )}*/}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // For desktop: use Tooltip
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="cursor-pointer"
            onClick={(e) => e.stopPropagation()} // Prevent click from propagating
          >
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" className="w-80 p-4 bg-white rounded-xl shadow-lg border-0 z-[100]">
          <CollectionContent />
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export function getCollectionOpenSeaUrl(
  collection: NFTCollection | { id: string; name: string; image?: string; chain?: "ethereum" | "base" | "ronin" },
): string {
  return getOpenSeaUrl(collection)
}
